import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { updatePrimaryPalette } from '@primeuix/themes';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly THEME_STORAGE_KEY = 'userThemeColor';
  public static readonly DEFAULT_THEME_COLOR = '#3e7b27';
  private themeColorSubject = new BehaviorSubject<string>(ThemeService.DEFAULT_THEME_COLOR);
  public themeColor$: Observable<string> = this.themeColorSubject.asObservable();

  constructor() {
    const storedRaw = localStorage.getItem(this.THEME_STORAGE_KEY);
    const initial =
      (storedRaw != null ? this.resolvePrimaryColor(storedRaw) : null) ??
      ThemeService.DEFAULT_THEME_COLOR;
    this.themeColorSubject.next(initial);
    this.applyCssAndPrimePalette(initial);
  }

  /** Hex `#RGB` / `#RRGGBB` only (trimmed, optional `#`). Invalid → null. */
  normalizeHexColor(color: string): string | null {
    if (color == null || typeof color !== 'string') {
      return null;
    }
    let c = color.trim();
    if (!c) {
      return null;
    }
    if (!c.startsWith('#')) {
      c = `#${c}`;
    }
    const raw = c.slice(1);
    if (!/^[a-fA-F0-9]{3}$|^[a-fA-F0-9]{6}$/.test(raw)) {
      return null;
    }
    if (raw.length === 3) {
      return `#${raw[0]}${raw[0]}${raw[1]}${raw[1]}${raw[2]}${raw[2]}`.toLowerCase();
    }
    return `#${raw.toLowerCase()}`;
  }

  /**
   * Hex first, then browser color parsing (handles rgb(), hsl(), named colors from API/UI).
   */
  private resolvePrimaryColor(raw: string): string | null {
    const fromHex = this.normalizeHexColor(raw);
    if (fromHex) {
      return fromHex;
    }
    const trimmed = raw.trim();
    if (!trimmed || typeof document === 'undefined') {
      return null;
    }
    const probe = document.createElement('span');
    probe.style.cssText =
      'position:absolute;left:-9999px;top:-9999px;visibility:hidden;pointer-events:none';
    probe.style.color = trimmed;
    document.body.appendChild(probe);
    const computedRgb = getComputedStyle(probe).color;
    document.body.removeChild(probe);

    const m = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/.exec(computedRgb);
    if (!m) {
      return null;
    }
    const r = Number(m[1]);
    const g = Number(m[2]);
    const b = Number(m[3]);
    return `#${this.toHexByte(r)}${this.toHexByte(g)}${this.toHexByte(b)}`;
  }

  /** Canonical hex for graphs: inline CSS token first (always set when theme applied), then subject. */
  getThemeColor(): string {
    if (typeof document !== 'undefined') {
      const css = document.documentElement.style.getPropertyValue('--theme-standardgreen').trim();
      if (css.length > 0) {
        const fromCss =
          this.resolvePrimaryColor(css) ?? this.normalizeHexColor(css);
        if (fromCss) {
          return fromCss;
        }
      }
    }
    return this.themeColorSubject.value;
  }

  setThemeColor(color: string): void {
    const resolved = this.resolvePrimaryColor(String(color).trim()) ?? ThemeService.DEFAULT_THEME_COLOR;

    localStorage.setItem(this.THEME_STORAGE_KEY, resolved);
    this.themeColorSubject.next(resolved);
    this.applyCssAndPrimePalette(resolved);
  }

  /** Resync CSS + Prime from subject; re-emits so dashboard/chart subscribers refresh. */
  reapplyTheme(): void {
    const v = this.themeColorSubject.value;
    this.applyCssAndPrimePalette(v);
    this.themeColorSubject.next(v);
  }

  resetTheme(): void {
    this.setThemeColor(ThemeService.DEFAULT_THEME_COLOR);
  }

  /**
   * Update visuals only (preview). Does not overwrite localStorage.
   * Still uses same resolution pipeline as persisted theme.
   */
  applyTheme(color: string): void {
    const resolved =
      this.resolvePrimaryColor(String(color).trim()) ?? ThemeService.DEFAULT_THEME_COLOR;
    this.applyCssAndPrimePalette(resolved);
  }

  /** DOM variables + PrimeNG palette only (no BehaviorSubject). */
  private applyCssAndPrimePalette(normalizedHex: string): void {
    const root = document.documentElement;
    root.style.setProperty('--theme-standardgreen', normalizedHex);
    const lightColor = this.generateLightColor(normalizedHex);
    root.style.setProperty('--theme-lightgreen', lightColor);
    const palette = this.generateColorPalette(normalizedHex);
    try {
      updatePrimaryPalette(palette);
    } catch (e) {
      console.warn('ThemeService: updatePrimaryPalette failed', e);
    }
  }

  private generateLightColor(color: string): string {
    const hex = color.replace(/^#/, '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    if ([r, g, b].some((n) => Number.isNaN(n))) {
      return ThemeService.DEFAULT_THEME_COLOR;
    }
    const lightR = Math.round(r * 0.15 + 255 * 0.85);
    const lightG = Math.round(g * 0.15 + 255 * 0.85);
    const lightB = Math.round(b * 0.15 + 255 * 0.85);
    return `#${this.toHexByte(lightR)}${this.toHexByte(lightG)}${this.toHexByte(lightB)}`;
  }

  private toHexByte(value: number): string {
    const hex = Math.min(255, Math.max(0, value)).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }

  private generateColorPalette(baseColor: string): { [key: number]: string } {
    const hex = baseColor.replace(/^#/, '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    if ([r, g, b].some((n) => Number.isNaN(n))) {
      return this.generateColorPalette(ThemeService.DEFAULT_THEME_COLOR);
    }
    return {
      50: this.mixWithWhite(r, g, b, 0.95),
      100: this.mixWithWhite(r, g, b, 0.9),
      200: this.mixWithWhite(r, g, b, 0.8),
      300: this.mixWithWhite(r, g, b, 0.7),
      400: this.mixWithWhite(r, g, b, 0.5),
      500: baseColor,
      600: this.darkenColor(r, g, b, 0.15),
      700: this.darkenColor(r, g, b, 0.3),
      800: this.darkenColor(r, g, b, 0.45),
      900: this.darkenColor(r, g, b, 0.6),
      950: this.darkenColor(r, g, b, 0.75),
    };
  }

  private mixWithWhite(r: number, g: number, b: number, whiteRatio: number): string {
    const colorRatio = 1 - whiteRatio;
    const newR = Math.round(r * colorRatio + 255 * whiteRatio);
    const newG = Math.round(g * colorRatio + 255 * whiteRatio);
    const newB = Math.round(b * colorRatio + 255 * whiteRatio);
    return `#${this.toHexByte(newR)}${this.toHexByte(newG)}${this.toHexByte(newB)}`;
  }

  private darkenColor(r: number, g: number, b: number, darkenRatio: number): string {
    const newR = Math.round(r * (1 - darkenRatio));
    const newG = Math.round(g * (1 - darkenRatio));
    const newB = Math.round(b * (1 - darkenRatio));
    return `#${this.toHexByte(newR)}${this.toHexByte(newG)}${this.toHexByte(newB)}`;
  }
}
